import { makeStyles, Button, Container, Grid, Typography, Theme, createStyles, Hidden, Box } from '@material-ui/core';
import { useState } from 'react';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { CheckMark } from '../../../icons/CheckMark';
import { DownloadIcon } from '../../../icons/DownloadIcon';
import { getQualityScore } from '../Quality/getQualityScore/getQualityScore';
import { QualityScore } from '../Quality/Quality';
// Note: Quality result is read from state.qualityResult (computed once in AppStateProvider)
import { QualityModal } from '../Quality/QualityModal/QualityModal';
import { ConnectionModal } from '../Connectivity/ConnectionModal/ConnectionModal';
import { SmallError } from '../../../icons/SmallError';
import { SmallWarning } from '../../../icons/SmallWarning';
import { getTestHistory, TestHistoryEntry } from '../../../utils/testHistory';
import SomeFailed from './SomeFailed.png';
import TestsPassed from './TestsPassed.png';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      float: 'left',
      [theme.breakpoints.down('md')]: {
        float: 'initial',
        paddingBottom: '1em',
      },
    },
    buttonContainer: {
      display: 'inline-flex',
      flexWrap: 'wrap',
      gap: '1em',
      width: '100%',
    },
    resultsList: {
      float: 'right',
      [theme.breakpoints.down('md')]: {
        float: 'initial',
      },
    },
    resultContainer: {
      marginTop: '1.5em',
      '&:not(:last-child)': {
        paddingBottom: '1.5em',
        borderBottom: '0.1em solid #8891AA',
      },
      [theme.breakpoints.down('md')]: {
        '&:last-child': {
          paddingBottom: '1.5em',
        },
      },
    },
    iconContainer: {
      display: 'flex',
      '& svg': {
        margin: '0.2em 0.8em 0 0',
      },
    },
    downloadButton: {
      '& svg': {
        position: 'relative',
        left: '-5px',
      },
    },
    restartButton: {
      backgroundColor: '#FFFFFF',
      borderColor: '#8891AA',
    },
    illustration: {
      marginTop: '7em',
    },
    hardwareButton: {
      marginRight: '1.5em',
    },
    gutterBottom: {
      marginBottom: '1em',
      [theme.breakpoints.down('md')]: {
        marginBottom: '1.5em',
      },
    },
    historyContainer: {
      clear: 'both',
      marginTop: '2em',
      padding: '1em',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e9ecef',
    },
    historyTable: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: '0.85rem',
      '& th': {
        textAlign: 'left' as const,
        padding: '6px 8px',
        borderBottom: '1px solid #dee2e6',
        fontWeight: 600,
        color: '#495057',
      },
      '& td': {
        padding: '6px 8px',
        borderBottom: '1px solid #f1f3f5',
        color: '#212529',
      },
    },
    historyResult: {
      fontWeight: 500,
    },
  })
);

// 3-tier result classification
type ResultTier = 'passed' | 'warning' | 'poor';

function getResultTier(score: QualityScore): ResultTier {
  if (score === QualityScore.Excellent || score === QualityScore.Good) return 'passed';
  if (score === QualityScore.Suboptimal) return 'warning';
  return 'poor';
}

export function Results() {
  const { state, downloadFinalTestResults, dispatch } = useAppStateContext();
  // Use pre-computed quality result from state (computed once via useEffect in AppStateProvider)
  const { totalQualityScore, latency, jitter, packetLoss, bitrate, mos } =
    state.qualityResult ?? getQualityScore(state.preflightTest.report, state.bitrateTest.report);
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);
  const [isConnectivityModalOpen, setIsConnectivityModalOpen] = useState(false);
  const classes = useStyles();

  // Connectivity status information for the modal
  const signalingGateway = state.preflightTest.signalingGatewayReachable ? 'Reachable' : 'Unreachable';
  const turnServers = state.preflightTest.turnServersReachable ? 'Reachable' : 'Unreachable';

  const resultTier = getResultTier(totalQualityScore);
  const qualityScore = QualityScore[totalQualityScore].toLowerCase();

  // 3-tier heading
  const heading =
    resultTier === 'passed' ? 'All tests passed!' :
    resultTier === 'warning' ? 'Tests completed with warnings' :
    'Some tests need attention';

  // 3-tier quality icon
  const qualityIcon =
    resultTier === 'passed' ? <CheckMark /> :
    resultTier === 'warning' ? <SmallWarning /> :
    <SmallError />;

  return (
    <>
      <QualityModal
        isModalOpen={isQualityModalOpen}
        setIsModalOpen={setIsQualityModalOpen}
        jitter={jitter}
        latency={latency}
        packetLoss={packetLoss}
        bitrate={bitrate}
        mos={mos}
      />
      <ConnectionModal
        isModalOpen={isConnectivityModalOpen}
        setIsModalOpen={setIsConnectivityModalOpen}
        serviceStatuses={state.twilioStatus}
        signalingGateway={signalingGateway}
        turnServers={turnServers}
      />
      <Container>
        <div>
          <Grid item lg={5} className={classes.header}>
            <Typography variant="h1" gutterBottom>
              {heading}
            </Typography>

            {resultTier === 'passed' ? (
              <Typography variant="body1" gutterBottom>
                As far as we can tell, your calling should be working. If you're still experiencing issues, download your
                report results and send to CloudTalk customer support.
              </Typography>
            ) : resultTier === 'warning' ? (
              <Typography variant="body1" gutterBottom>
                Your connection should work for calls, but call quality may be affected during peak usage or on longer
                calls. Download the report results and share with CloudTalk customer support if you experience issues.
              </Typography>
            ) : (
              <Typography variant="body1" gutterBottom>
                Some test results indicate potential call quality issues. Use this list to troubleshoot common problems
                and restart the test. If you can't easily solve the problem(s), download report results and send to
                CloudTalk customer support.
              </Typography>
            )}

            <div className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                className={classes.downloadButton}
                onClick={downloadFinalTestResults}
              >
                <DownloadIcon />
                Download report results
              </Button>
              <Button variant="outlined" className={classes.restartButton} onClick={() => window.location.reload()}>
                Test again
              </Button>
            </div>

            <Hidden mdDown>
              <img
                src={resultTier === 'passed' ? TestsPassed : SomeFailed}
                alt={resultTier === 'passed' ? 'Success' : 'Some Failed'}
                className={classes.illustration}
              />
            </Hidden>
          </Grid>

          <Grid item lg={5} className={classes.resultsList}>
            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                <CheckMark />
                <Typography variant="h3" className={classes.gutterBottom}>
                  Device &amp; Network Setup
                </Typography>
              </div>
              <Typography variant="body1" className={classes.gutterBottom}>
                Audio successfully received from your hardware and browser.
              </Typography>

              <div className={classes.buttonContainer}>
                <Button
                  variant="outlined"
                  onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.CameraTest })}
                >
                  Review hardware
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.BrowserTest })}
                >
                  Review browser
                </Button>
              </div>
            </div>

            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                <CheckMark />
                <Typography variant="h3" className={classes.gutterBottom}>
                  Connectivity
                </Typography>
              </div>
              <Typography variant="body1" className={classes.gutterBottom}>
                All connections are working successfully.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setIsConnectivityModalOpen(true)}
              >
                Review connectivity
              </Button>
            </div>

            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                {qualityIcon}
                <Typography variant="h3" className={classes.gutterBottom}>
                  Quality &amp; Performance
                </Typography>
              </div>

              {resultTier === 'passed' ? (
                <Typography variant="body1" className={classes.gutterBottom}>
                  Awesome! Your expected call quality is <strong>{qualityScore}</strong> and overall performance looks
                  {qualityScore === 'excellent' ? ' good' : ' ok'}.
                </Typography>
              ) : resultTier === 'warning' ? (
                <Typography variant="body1" className={classes.gutterBottom}>
                  Your call quality score is <strong>{qualityScore}</strong>. Calls should work, but you may notice
                  occasional audio issues under heavy network load.
                </Typography>
              ) : (
                <Typography variant="body1" className={classes.gutterBottom}>
                  Your overall score is <strong>{qualityScore}</strong> which means that your connection may not be
                  reliable enough for calls. Try the tips below and rerun the test.
                </Typography>
              )}

              <Button
                variant="outlined"
                onClick={() => setIsQualityModalOpen(true)}
              >
                Review performance
              </Button>
            </div>
          </Grid>

          {/* Previous test results history */}
          <PreviousResults classes={classes} />
        </div>
      </Container>
    </>
  );
}

/**
 * Display previous test results from localStorage.
 * Only shown if there are past results (besides the current one).
 */
function PreviousResults({ classes }: { classes: Record<string, string> }) {
  const history = getTestHistory();

  // Show history only if there are at least 2 entries (current + past)
  if (history.length < 2) return null;

  // Skip the first entry (it's the current test) — show only previous ones
  const previousResults = history.slice(1);

  const formatDate = (timestamp: string) => {
    try {
      const d = new Date(timestamp);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return timestamp;
    }
  };

  const getResultColor = (result: string) => {
    if (result === 'PASS') return '#0E7C3A';
    if (result === 'WARNING') return '#F5A623';
    return '#D61F1F';
  };

  return (
    <Box className={classes.historyContainer}>
      <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 600 }}>
        Previous Test Results
      </Typography>
      <table className={classes.historyTable}>
        <thead>
          <tr>
            <th>Date</th>
            <th>Result</th>
            <th>Quality</th>
            <th>MOS</th>
            <th>Latency</th>
            <th>Loss</th>
          </tr>
        </thead>
        <tbody>
          {previousResults.map((entry: TestHistoryEntry, i: number) => (
            <tr key={i}>
              <td>{formatDate(entry.timestamp)}</td>
              <td>
                <span className={classes.historyResult} style={{ color: getResultColor(entry.overallResult) }}>
                  {entry.overallResult}
                </span>
              </td>
              <td>{entry.qualityScore}</td>
              <td>{entry.mosScore ?? '—'}</td>
              <td>{entry.latencyAvg ? `${entry.latencyAvg}ms` : '—'}</td>
              <td>{entry.packetLossAvg ? `${entry.packetLossAvg}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}
